// メインのJavaScriptファイル
import 'swiper/css/bundle';
import 'intl-tel-input/styles';
import '../scss/style.scss';
import Swiper from 'swiper/bundle';
import intlTelInput from 'intl-tel-input';

// コメント入力フォーム 文字カウンタ（Figma 2666:17752）
//   data-max を超えるとカウンタを赤に、送信ボタンを disable。空文字も disable。
//   サロゲートペア（絵文字等）を 1 文字として数えるため Array.from で長さを取る。
function soysourceInitCommentCharCounter() {
    const forms = document.querySelectorAll('[data-soysource-comment-form]');
    if (!forms.length) return;
    forms.forEach((form) => {
        const textarea = form.querySelector('[data-soysource-comment-input]');
        const counter = form.closest('.soysource-comment-form')?.querySelector('.soysource-comment-form__counter');
        const current = counter?.querySelector('[data-soysource-comment-count]');
        const submit = form.querySelector('[data-soysource-comment-submit]');
        if (!textarea || !counter || !current) return;
        const max = parseInt(textarea.getAttribute('data-max') || '500', 10) || 500;
        const update = () => {
            const len = Array.from(textarea.value).length;
            current.textContent = String(len);
            const over = len > max;
            counter.classList.toggle('soysource-comment-form__counter--over', over);
            if (submit) submit.disabled = over || len === 0;
        };
        textarea.addEventListener('input', update);
        update();
    });
}

// コメント リアクション（共感した / 考えさせられた / なるほど）
//   POST /wp-json/soysource/v1/comments/{id}/reactions { type, action: 'toggle' }
//   サーバが setcookie で押下状態を保持。クライアントはレスポンスを反映するだけ。
function soysourceInitCommentReactions() {
    const buttons = document.querySelectorAll('.soysource-comment-reaction[data-soysource-reaction]');
    if (!buttons.length) return;
    const cfg = window.soysourceCommentReactions;
    if (!cfg || !cfg.endpoint) return;

    const buildUrl = (cid) => cfg.endpoint.replace('__ID__', encodeURIComponent(String(cid)));

    const applyResponse = (cid, data) => {
        if (!data || typeof data !== 'object') return;
        const counts = data.counts || {};
        const pressed = Array.isArray(data.pressed) ? data.pressed : [];
        const itemButtons = document.querySelectorAll(
            `.soysource-comment-reaction[data-comment-id="${CSS.escape(String(cid))}"]`
        );
        itemButtons.forEach((b) => {
            const t = b.dataset.soysourceReaction;
            const cnt = b.querySelector('[data-soysource-reaction-count]');
            if (cnt) {
                const n = Math.max(0, Number(counts[t] || 0));
                cnt.textContent = n.toLocaleString('ja-JP');
                if (n > 0) {
                    cnt.removeAttribute('hidden');
                } else {
                    cnt.setAttribute('hidden', '');
                }
            }
            b.setAttribute('aria-pressed', pressed.includes(t) ? 'true' : 'false');
        });
    };

    buttons.forEach((btn) => {
        btn.addEventListener('click', async () => {
            if (btn.disabled) return;
            const cid = btn.dataset.commentId;
            const type = btn.dataset.soysourceReaction;
            if (!cid || !type) return;

            btn.disabled = true;
            btn.classList.add('is-loading');
            try {
                const res = await fetch(buildUrl(cid), {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': cfg.nonce || '',
                    },
                    body: JSON.stringify({ type, action: 'toggle' }),
                });
                if (!res.ok) {
                    throw new Error('reaction request failed: ' + res.status);
                }
                const data = await res.json();
                applyResponse(cid, data);
            } catch (e) {
                console.error('[soysource] reaction error:', e);
            } finally {
                btn.disabled = false;
                btn.classList.remove('is-loading');
            }
        });
    });
}

// 国際電話番号入力（intl-tel-input v29 / form_03 定期購読 ほか）
//   - `[data-soysource-intl-tel]` を持つ <input type="tel"> に国旗ドロップダウンを装着
//   - 入力時に E.164 形式（+18005551234 等）を hidden input（name = data-soysource-intl-tel-hidden）に同期
//   - utils（バリデーション / フォーマット）は dynamic import で遅延読込
function soysourceInitIntlTel() {
    const inputs = document.querySelectorAll('input[data-soysource-intl-tel]');
    if (!inputs.length) return;

    inputs.forEach((input) => {
        const hiddenName = input.dataset.soysourceIntlTelHidden;
        const form = input.closest('form');
        const hidden = hiddenName && form
            ? form.querySelector(`input[type="hidden"][name="${CSS.escape(hiddenName)}"]`)
            : null;

        try {
            const iti = intlTelInput(input, {
                initialCountry: 'us',
                separateDialCode: true,
                strictMode: true,
                loadUtils: () => import('intl-tel-input/utils'),
            });

            const sync = () => {
                if (!hidden) return;
                // utils 読込後は E.164、未読込時は素の入力 + 国番号を結合した近似値
                if (typeof iti.getNumber === 'function') {
                    const e164 = iti.getNumber();
                    hidden.value = e164 || input.value;
                } else {
                    hidden.value = input.value;
                }
            };

            input.addEventListener('input', sync);
            input.addEventListener('countrychange', sync);
            if (form) form.addEventListener('submit', sync);
            sync();
        } catch (e) {
            console.error('[soysource] intl-tel-input init error:', e);
        }
    });
}

// フォーム合計金額（newyear / subscribe 共通）
//   - form 内の `input[data-price]`（radio/checkbox）から合計を算出
//   - `[data-newyear-total]` / `[data-subscribe-total]` を持つ <span> に "$N.NN" を書き込む
//   - radio は同 name グループのうち checked のみカウント、checkbox は単独で加算
function soysourceInitFormTotal(form, totalEl) {
    const inputs = form.querySelectorAll('input[data-price]');
    if (!inputs.length) return;

    const update = () => {
        let total = 0;
        inputs.forEach((input) => {
            if (input.disabled) return;
            if (input.type !== 'radio' && input.type !== 'checkbox') return;
            if (!input.checked) return;
            const p = parseFloat(input.dataset.price);
            if (!Number.isNaN(p)) total += p;
        });
        totalEl.textContent = '$' + total.toFixed(2);
    };

    form.addEventListener('change', (e) => {
        if (e.target && e.target.matches && e.target.matches('input[data-price]')) {
            update();
        }
    });
    update();
}

function soysourceInitFormTotals() {
    document.querySelectorAll('[data-newyear-total], [data-subscribe-total]').forEach((totalEl) => {
        const form = totalEl.closest('form');
        if (form) soysourceInitFormTotal(form, totalEl);
    });
    document.querySelectorAll('[data-sakura-total]').forEach((totalEl) => {
        const form = totalEl.closest('form');
        if (form) soysourceInitSakuraFormTotal(form, totalEl);
    });
}

// 桜祭号フォーム（form_11）— 早割期限に応じて data-price / data-early-price を切替
function soysourceInitSakuraFormTotal(form, totalEl) {
    const inputs = form.querySelectorAll('input[data-price][data-early-price]');
    if (!inputs.length) return;

    const resolveEarlyActive = () => {
        if (form.dataset.earlyActive === '1') return true;
        if (form.dataset.earlyActive === '0') return false;
        const deadline = form.dataset.earlyDeadline;
        if (!deadline) return false;
        const end = new Date(`${deadline}T23:59:59`);
        return !Number.isNaN(end.getTime()) && Date.now() <= end.getTime();
    };

    const getPrice = (input) => {
        const useEarly = resolveEarlyActive();
        const raw = useEarly ? input.dataset.earlyPrice : input.dataset.price;
        const p = parseFloat(raw);
        return Number.isNaN(p) ? 0 : p;
    };

    const update = () => {
        let total = 0;
        inputs.forEach((input) => {
            if (input.disabled) return;
            if (input.type !== 'radio' && input.type !== 'checkbox') return;
            if (!input.checked) return;
            total += getPrice(input);
        });
        totalEl.textContent = '$' + total.toFixed(2);
    };

    form.addEventListener('change', (e) => {
        if (e.target && e.target.matches && e.target.matches('input[data-price][data-early-price]')) {
            update();
        }
    });
    update();
}

// 有料クラシファイド申込フォーム（form_07）
//   - 文字数 radio に応じて掲載文の maxlength / カウンタ上限 / ラベル補足を更新
//   - 合計 = (掲載料 data-price + 太字 data-price) × 掲載月数
function soysourceInitClsfPaidForm() {
    const form = document.getElementById('soysource-clsf-paid-form');
    if (!form) return;

    const textarea = form.querySelector('#soysource-clsf-paid-body');
    const totalEl = form.querySelector('[data-clsf-paid-total]');
    const counterCurrent = form.querySelector('[data-counter-current]');
    const counterMax = form.querySelector('[data-clsf-paid-counter-max]');
    const bodySub = form.querySelector('[data-clsf-paid-body-sub]');
    const boldCheckbox = form.querySelector('input[name="clsf_paid[bold]"]');
    const lengthInputs = form.querySelectorAll('input[name="clsf_paid[length]"]');
    const periodInputs = form.querySelectorAll('input[name="clsf_paid[period]"]');

    const getChecked = (inputs) => Array.from(inputs).find((input) => input.checked);

    const getSelectedLength = () => {
        const checked = getChecked(lengthInputs);
        if (!checked) {
            return { max: 40, price: 0, label: '' };
        }
        return {
            max: parseInt(checked.value, 10) || 40,
            price: parseFloat(checked.dataset.price) || 0,
            label: checked.dataset.lengthLabel || '',
        };
    };

    const getSelectedMonths = () => {
        const checked = getChecked(periodInputs);
        if (!checked) return 1;
        if (checked.value === '6plus') return 6;
        const months = parseInt(checked.value, 10);
        return Number.isNaN(months) ? 1 : months;
    };

    const formatMoney = (amount) => '$' + amount.toFixed(2);

    const updateCounter = () => {
        if (!textarea || !counterCurrent) return;
        const len = Array.from(textarea.value).length;
        const { max } = getSelectedLength();
        counterCurrent.textContent = String(len);
        const counter = counterCurrent.closest('.soysource-clsf-paid-form__counter');
        if (counter) {
            counter.classList.toggle('soysource-clsf-paid-form__counter--over', len > max);
        }
    };

    const updateLengthUI = () => {
        const { max, price, label } = getSelectedLength();
        if (textarea) {
            textarea.maxLength = max;
            textarea.setAttribute('maxlength', String(max));
        }
        if (counterMax) {
            counterMax.textContent = '/' + max + '文字';
        }
        if (bodySub && label) {
            bodySub.textContent = '（' + label + ' ($' + price.toFixed(2) + '/month)）';
        }
        updateCounter();
    };

    const updateTotal = () => {
        if (!totalEl) return;
        const { price: lengthPrice } = getSelectedLength();
        let monthly = lengthPrice;
        if (boldCheckbox && boldCheckbox.checked) {
            const boldPrice = parseFloat(boldCheckbox.dataset.price);
            if (!Number.isNaN(boldPrice)) monthly += boldPrice;
        }
        totalEl.textContent = formatMoney(monthly * getSelectedMonths());
    };

    form.addEventListener('change', (e) => {
        if (!e.target || !e.target.matches) return;
        if (e.target.matches('input[name="clsf_paid[length]"]')) {
            updateLengthUI();
        }
        if (
            e.target.matches('input[name="clsf_paid[length]"]')
            || e.target.matches('input[name="clsf_paid[period]"]')
            || e.target.matches('input[name="clsf_paid[bold]"]')
        ) {
            updateTotal();
        }
    });

    if (textarea) {
        textarea.addEventListener('input', updateCounter);
    }

    updateLengthUI();
    updateTotal();
}

// ユーティリティバー時刻リアルタイム更新
function soysourceUpdateClocks() {
    const clocks = document.querySelectorAll('.soysource-utility-bar__clock .soysource-utility-bar__time');
    if (!clocks.length) return;
    const now = new Date();
    const dowShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    clocks.forEach((el) => {
        const tz = el.closest('.soysource-utility-bar__clock')?.dataset.timezone || 'UTC';
        const dt = new Date(now.toLocaleString('en-US', { timeZone: tz }));
        const h = dt.getHours();
        const ampm = h >= 12 ? 'PM' : 'AM';
        const h12 = h % 12 || 12;
        const pad = (n) => String(n).padStart(2, '0');
        const str = `${dt.getFullYear()} / ${pad(dt.getMonth() + 1)} / ${pad(dt.getDate())} ${dowShort[dt.getDay()]} ${pad(h12)}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())} ${ampm}`;
        el.textContent = str;
        el.setAttribute('datetime', dt.toISOString());
    });
}

// アカウント削除モーダル（Figma 2699-42071）
function soysourceInitAccountDeleteModal() {
    const modal = document.querySelector('[data-soysource-account-delete-modal]');
    if (!modal) return;

    const dialog = modal.querySelector('.soysource-account-delete-modal__dialog');
    const openTriggers = document.querySelectorAll('[data-soysource-account-delete-open]');
    const closeTriggers = modal.querySelectorAll('[data-soysource-account-delete-close]');
    let lastFocused = null;

    const setOpen = (open) => {
        modal.classList.toggle('is-open', open);
        modal.hidden = !open;
        document.body.classList.toggle('soysource-account-delete-modal-open', open);

        if (open) {
            lastFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
            dialog?.focus();
            return;
        }

        if (lastFocused) {
            lastFocused.focus();
            lastFocused = null;
        }
    };

    openTriggers.forEach((trigger) => {
        trigger.addEventListener('click', () => setOpen(true));
    });

    closeTriggers.forEach((trigger) => {
        trigger.addEventListener('click', () => setOpen(false));
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && modal.classList.contains('is-open')) {
            event.preventDefault();
            setOpen(false);
        }
    });

    if (modal.hasAttribute('data-soysource-account-delete-auto-open')) {
        setOpen(true);
    }
}

// DOMContentLoadedイベントで初期化
document.addEventListener('DOMContentLoaded', function() {
    // ユーティリティバー時計
    const clockEls = document.querySelectorAll('.soysource-utility-bar__clock');
    if (clockEls.length) {
        soysourceUpdateClocks();
        setInterval(soysourceUpdateClocks, 1000);
    }

    // コメント入力フォーム文字カウンタ
    soysourceInitCommentCharCounter();

    // コメント リアクションボタン
    soysourceInitCommentReactions();

    // フォーム合計金額（form_01 newyear / form_03 subscribe / form_11 sakura）
    soysourceInitFormTotals();

    // 有料クラシファイド申込（form_07）
    soysourceInitClsfPaidForm();

    // 国際電話番号入力（intl-tel-input / form_03 subscribe ほか）
    soysourceInitIntlTel();

    // アカウント削除モーダル（user_03 / 2699-42071）
    soysourceInitAccountDeleteModal();

    // モバイルメニューのトグル
    const mobileMenuToggle = document.querySelector('.mobile-menu-toggle');
    const mobileMenu = document.querySelector('.mobile-menu');
    if (mobileMenuToggle && mobileMenu) {
        mobileMenuToggle.addEventListener('click', function() {
            mobileMenu.classList.toggle('active');
        });
    }

    // SP 全画面ドロワー（Figma 2645-14930）
    const drawerToggle = document.getElementById('soysource-drawer-toggle');
    const drawer = document.getElementById('soysource-drawer');
    if (drawerToggle && drawer) {
        const openLabel = drawerToggle.getAttribute('aria-label') || 'メニューを開く';
        const closeLabel = 'メニューを閉じる';

        function soysourceSetDrawerOpen(open) {
            drawer.classList.toggle('is-open', open);
            document.body.classList.toggle('soysource-drawer-open', open);
            drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
            drawerToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
            drawerToggle.setAttribute('aria-label', open ? closeLabel : openLabel);
        }

        drawerToggle.addEventListener('click', function() {
            soysourceSetDrawerOpen(!drawer.classList.contains('is-open'));
        });

        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
                soysourceSetDrawerOpen(false);
            }
        });
    }

    const drawerCatSelect = document.getElementById('soysource-drawer-cat');
    if (drawerCatSelect) {
        drawerCatSelect.addEventListener('change', function () {
            if (this.value) {
                window.location.href = this.value;
            }
        });
    }

    // メインナビ直下カルーセル（Swiper Centered + Autoplay）
    const carouselEl = document.getElementById('soysource-nav-carousel');
    if (carouselEl) {
        try {
            const paginationEl = carouselEl.querySelector('.swiper-pagination');
            new Swiper(carouselEl, {
                loop: true,
                centeredSlides: true,
                slidesPerView: 'auto',
                slidesPerGroup: 1,
                spaceBetween: 20,
                speed: 600,
                autoplay: {
                    delay: 3000,
                    disableOnInteraction: false,
                },
                pagination: paginationEl ? { el: paginationEl, clickable: true } : false,
                breakpoints: {
                    768: {
                        slidesPerView: 'auto',
                        spaceBetween: 20,
                    },
                },
            });
        } catch (e) {
            console.error('Swiper carousel init error:', e);
        }
    }

    // 写真で見るクラシファイド（Figma node-id: 2645-9824）
    const classifiedPhotosEl = document.getElementById('soysource-classified-photos');
    if (classifiedPhotosEl) {
        try {
            const carouselEl = classifiedPhotosEl.closest('.soysource-classified-photos__carousel');
            const prevEl = carouselEl?.querySelector('.soysource-classified-photos__prev');
            const nextEl = carouselEl?.querySelector('.soysource-classified-photos__next');
            new Swiper(classifiedPhotosEl, {
                slidesPerView: 'auto',
                spaceBetween: 16,
                navigation: prevEl && nextEl ? { prevEl, nextEl, addIcons: false } : false,
            });
        } catch (e) {
            console.error('Swiper classified photos init error:', e);
        }
    }

    // 写真で見るイベントカレンダー（クラシファイド同様）
    const eventPhotosEl = document.getElementById('soysource-event-photos');
    if (eventPhotosEl) {
        try {
            const carouselEl = eventPhotosEl.closest('.soysource-classified-photos__carousel');
            const prevEl = carouselEl?.querySelector('.soysource-classified-photos__prev');
            const nextEl = carouselEl?.querySelector('.soysource-classified-photos__next');
            new Swiper(eventPhotosEl, {
                slidesPerView: 'auto',
                spaceBetween: 16,
                navigation: prevEl && nextEl ? { prevEl, nextEl, addIcons: false } : false,
            });
        } catch (e) {
            console.error('Swiper event photos init error:', e);
        }
    }

    // ソイソースセレクト カード内スライドショー（複数画像時・自動再生）
    const initSelectCardSwiper = (selector, carouselClass, prevClass, nextClass) => {
        document.querySelectorAll(selector).forEach((swiperEl) => {
            try {
                const carouselEl = swiperEl.closest(carouselClass);
                const prevEl = carouselEl?.querySelector(prevClass);
                const nextEl = carouselEl?.querySelector(nextClass);
                new Swiper(swiperEl, {
                    loop: true,
                    slidesPerView: 1,
                    spaceBetween: 0,
                    autoplay: {
                        delay: 3000,
                        disableOnInteraction: false,
                    },
                    navigation: prevEl && nextEl ? { prevEl, nextEl, addIcons: false } : false,
                });
            } catch (e) {
                console.error('Swiper select card init error:', e);
            }
        });
    };

    initSelectCardSwiper(
        '.soysource-select-card__swiper',
        '.soysource-select-card__carousel',
        '.soysource-select-card__prev',
        '.soysource-select-card__next',
    );

    initSelectCardSwiper(
        '.soysource-select-single__hero-swiper',
        '.soysource-select-single__hero-carousel',
        '.soysource-select-single__hero-prev',
        '.soysource-select-single__hero-next',
    );

    // ファイル選択ラベル表示
    document.querySelectorAll('.soysource-dashboard-post-form__file-input').forEach((input) => {
        const nameEl = input.closest('.soysource-dashboard-post-form__field--file')?.querySelector('[data-soysource-file-name]');
        if (!nameEl) return;

        input.addEventListener('change', () => {
            const file = input.files && input.files[0];
            nameEl.textContent = file ? file.name : '';
        });
    });

    // ソイソースセレクト — 商品紹介セクションの追加・削除
    const selectProductsRoot = document.querySelector('[data-soysource-select-products]');
    const selectProductTemplate = document.getElementById('soysource-select-product-template');

    const reindexSelectProducts = () => {
        if (!selectProductsRoot) return;

        selectProductsRoot.querySelectorAll('[data-soysource-select-product]').forEach((block, index) => {
            block.dataset.productIndex = String(index);

            block.querySelectorAll('[name^="select_products["]').forEach((field) => {
                field.name = field.name.replace(/select_products\[\d+]/, `select_products[${index}]`);
            });

            block.querySelectorAll('[id^="soysource-sel-product-"]').forEach((field) => {
                field.id = field.id.replace(/soysource-sel-product-\d+/, `soysource-sel-product-${index}`);
            });

            block.querySelectorAll('label[for^="soysource-sel-product-"]').forEach((label) => {
                label.htmlFor = label.htmlFor.replace(/soysource-sel-product-\d+/, `soysource-sel-product-${index}`);
            });

            const removeBtn = block.querySelector('[data-soysource-select-product-remove]');
            if (removeBtn) {
                removeBtn.hidden = index === 0;
            }
        });
    };

    if (selectProductsRoot && selectProductTemplate) {
        document.querySelector('[data-soysource-select-add-product]')?.addEventListener('click', () => {
            const index = selectProductsRoot.querySelectorAll('[data-soysource-select-product]').length;
            const html = selectProductTemplate.innerHTML.replace(/__INDEX__/g, String(index));
            const wrapper = document.createElement('div');
            wrapper.innerHTML = html.trim();
            const node = wrapper.firstElementChild;
            if (!node) return;

            selectProductsRoot.appendChild(node);

            node.querySelectorAll('.soysource-dashboard-post-form__file-input').forEach((input) => {
                const nameEl = input.closest('.soysource-dashboard-post-form__field--file')?.querySelector('[data-soysource-file-name]');
                if (!nameEl) return;
                input.addEventListener('change', () => {
                    const file = input.files && input.files[0];
                    nameEl.textContent = file ? file.name : '';
                });
            });

            reindexSelectProducts();
        });

        selectProductsRoot.addEventListener('click', (event) => {
            const removeBtn = event.target.closest('[data-soysource-select-product-remove]');
            if (!removeBtn) return;

            const block = removeBtn.closest('[data-soysource-select-product]');
            if (!block || selectProductsRoot.children.length <= 1) return;

            block.remove();
            reindexSelectProducts();
        });

        reindexSelectProducts();
    }
});
